import {faCheckSquare} from '@fortawesome/pro-duotone-svg-icons';
import {faSquare} from '@fortawesome/pro-regular-svg-icons';
import styled, {css} from 'styled-components';

import {Icon} from '../../components/Icon/Icon';
import {BorderButtonCss} from '../../_old/styled/helpers';
import {scToTwColour} from '../../theme';

import type {IconProp} from '@fortawesome/fontawesome-svg-core';

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onClick'> &
  Pick<React.InputHTMLAttributes<HTMLSpanElement>, 'onClick'> & {
    children: React.ReactNode;
    type: 'radio' | 'checkbox';
  };
export function Toggleable({
  type,
  children,
  className,
  onClick,
  title,
  ...restProps
}: Props): React.JSX.Element {
  const checkIcon: IconProp = restProps.checked ? faCheckSquare : faSquare;

  return (
    <Container title={title}>
      <Input {...restProps} type={type} />
      <ToggleableLabel
        className={className}
        checked={!!restProps.checked}
        disabled={!!restProps.disabled}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
      >
        {children}
        {type === 'checkbox' && (
          <StyledCheckboxIcon
            icon={checkIcon}
            checked={!!restProps.checked}
            disabled={!!restProps.disabled}
          />
        )}
      </ToggleableLabel>
    </Container>
  );
}

const Container = styled.label`
  position: relative;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: all;
`;
// !important to allow generic margin-right on Icons in BorderButtonCss
const StyledCheckboxIcon = styled(Icon)<{checked: boolean; disabled: boolean}>`
  margin-left: auto;
  margin-right: 0 !important;

  color: ${scToTwColour('theme.colours.slate11')};
  --fa-primary-color: ${scToTwColour('theme.colours.slate1')};
  --fa-secondary-color: ${scToTwColour('theme.colours.slate2')};
  --fa-secondary-opacity: 1;

  ${({checked, disabled}) => {
    if (checked && disabled) {
      return css`
        --fa-primary-color: ${scToTwColour('theme.colours.slate1')};
        --fa-secondary-color: ${scToTwColour('theme.colours.slate2')};
      `;
    } else if (checked) {
      return css`
        --fa-secondary-color: ${scToTwColour('theme.colours.blue10')};
      `;
    } else {
      return ``;
    }
  }}
`;
export const ToggleableLabel = styled.span<{
  checked: boolean;
  disabled: boolean;
}>`
  ${BorderButtonCss}
  display: flex;
  align-items: center;
  margin-right: 0;

  ${css`
    &:hover {
      ${StyledCheckboxIcon} {
        margin-left: auto;
      }
    }
  `}
  ${({disabled}) =>
    disabled &&
    css`
      cursor: unset;
      color: ${scToTwColour('theme.colours.slate11')};
      border-color: ${scToTwColour('theme.colours.slate6')};

      ._icon {
        color: ${scToTwColour('theme.colours.slate11')};
      }

      &:hover {
        border-color: ${scToTwColour('theme.colours.slate6')};
      }
      ${StyledCheckboxIcon} {
        visibility: hidden;
      }
    `}
    ${({checked, disabled}) =>
    checked &&
    disabled &&
    css`
      ${StyledCheckboxIcon} {
        visibility: visible;
      }

      &:hover {
        ${StyledCheckboxIcon} {
          margin-left: auto;
        }
      }
    `}
`;

const Input = styled.input`
  position: absolute;
  overflow: hidden;
  clip: rect(0 0 0 0);
  height: 1px;
  width: 1px;
  margin: -1px;
  padding: 0;
  border: 0;

  &:checked + ${ToggleableLabel} {
    border-color: ${scToTwColour('theme.colours.blue8')};
  }
  ${({disabled}) =>
    disabled &&
    css`
      &:checked + ${ToggleableLabel} {
        border-color: ${scToTwColour('theme.colours.slate6')};
      }
    `}
`;
